import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { FileViewerComponent } from '@shared/components/file-viewer/file-viewer.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ApiService } from '@shared/services/api.service';
import { ChangeOrderStatus, EntityType, EventType, GqlService } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';

export const injectChangeOrderBudgetUploadModal = (overlayService = inject(OverlayService)) => {
  return {
    open: (data: ChangeOrderBudgetUploadModalParams) => {
      return overlayService.openPopup<
        ChangeOrderBudgetUploadModalResponse,
        ChangeOrderBudgetUploadModalParams,
        ChangeOrderBudgetUploadModalComponent
      >({
        content: ChangeOrderBudgetUploadModalComponent,
        data,
        settings: {
          header: 'Upload Change Order Budget',
          primaryButton: {
            action: (instance) => instance?.onUpload(),
            label: 'Upload Change Order',
          },
        },
      });
    },
  };
};

export type ChangeOrderBudgetUploadModalParams = {
  change_order_id: string;
  change_order_status: ChangeOrderStatus;
  onSuccess: (processEventId: string) => void;
  organization_id: string;
};

export type ChangeOrderBudgetUploadModalResponse = unknown;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FileManagerComponent, FileViewerComponent, CheckboxComponent, FormsModule, NgClass],
  template: `
    <div class="mb-4">
      This will replace any existing Change Order Budget Updates made in this Change Order.
    </div>
    <div>
      <aux-file-manager
        #manager
        class="h-48"
        [fetchFilesOnInit]="false"
        [pathFn]="pathFn"
        [eager]="false"
        [showSuccessOnUpload]="true"
        [accept]="'.csv'"
      />
      <div class="max-h-60 overflow-auto" [ngClass]="{ 'mt-4': manager.fileQuery.count() > 0 }">
        <aux-file-viewer
          [fileManager]="manager"
          [disableFirstFileMargin]="true"
          [onlyShowUploaded]="false"
        />
      </div>
    </div>

    @if (errorMessage()) {
      <div class="mt-4 rounded-md bg-aux-error p-5 font-medium text-white">
        {{ errorMessage() }}
      </div>
    }

    <div class="mt-4">
      <aux-checkbox
        class="text-sm font-medium"
        [id]="'bypass-validation'"
        [(ngModel)]="bypassValidation"
      >
        Bypass blank Activity ID check
      </aux-checkbox>
    </div>
  `,
})
export class ChangeOrderBudgetUploadModalComponent {
  apiService = inject(ApiService);
  gqlService = inject(GqlService);
  mainQuery = inject(MainQuery);
  overlayService = inject(OverlayService);
  ref = inject(
    CustomOverlayRef<ChangeOrderBudgetUploadModalResponse, ChangeOrderBudgetUploadModalParams>
  );

  errorMessage = signal('');
  loading = signal(false);

  bypassValidation = model(false);

  fileManager = viewChild.required(FileManagerComponent);

  getFilePath(org_id: string, co_id: string) {
    const trial_id = this.mainQuery.getValue().trialKey;

    return `trials/${trial_id}/vendors/${org_id}/changeorders/${co_id}/`;
  }

  onUpload = async () => {
    this.errorMessage.set('');
    const fileManager = this.fileManager();

    const { change_order_id, change_order_status, organization_id } = this.ref.data;

    if (fileManager && !this.loading()) {
      const files = fileManager.fileQuery.getAll();

      if (!files.length) {
        this.errorMessage.set(MessagesConstants.FILE.NEED_UPLOAD_FILE);

        return;
      }

      if (files.length > 1) {
        this.errorMessage.set(MessagesConstants.FILE.MAX_ONE_FILE);

        return;
      }

      const match = files[0].bucket_key.match(/\.([^.]+)$/);

      if (match?.[1] !== 'csv') {
        this.errorMessage.set('File type must be a .csv!');

        return;
      }

      this.loading.set(true);

      const file = files[0];
      const bucket_key = `${this.getFilePath(organization_id, change_order_id)}${file.bucket_key}`;

      fileManager.fileStore.update(file.id, {
        ...file,
        bucket_key,
      });

      const fileSuccess = await fileManager.fileService.uploadFiles({ metadata: { admin: '1' } });

      if (fileSuccess) {
        const { data, errors, success } = await firstValueFrom(
          this.gqlService.validateEventFile$({
            bucket_key: `public/${bucket_key}`,
            entity_id: organization_id,
            entity_type: EntityType.ORGANIZATION,
            payload: JSON.stringify({
              change_order_id: change_order_id,
              change_order_status: change_order_status,
              skip_activityno_check: this.bypassValidation(),
            }),
            type: EventType.CHANGE_ORDER_BUDGET_TEMPLATE_UPLOADED,
          })
        );

        if (success && data?.id) {
          this.overlayService.success(`Budget is processing. Please wait...`);
          this.ref.data?.onSuccess(data.id);
        } else {
          void this.apiService.removeFile(bucket_key);
          this.overlayService.error(errors);
        }

        this.ref.close(true);
      }
    }

    this.loading.set(false);
  };

  pathFn: () => string = () => '';
}
